import { UserRole, WorkOrderStatus } from '@/types/models';
import type { Association, User, WorkOrder } from '@/types/models';
import { etDayMs, toBusinessMs, todayBusinessDate } from '@/utils/date-utils';

/**
 * Single source of truth for work-order field-level edit permissions.
 *
 * Consumed by BOTH the edit form (`components/work-orders/form.tsx`, to disable
 * inputs) and the API (`api/work-orders.ts`, to silently drop fields, and to
 * scope which work orders a property manager may edit). Keeping one module means
 * the UI never offers an edit the server will reject, and a crafted PUT can
 * never set a field the form hides.
 *
 * The module depends only on `@/types/models` and `@/utils/date-utils` so it
 * imports cleanly on both the Expo client and the Cloudflare Worker.
 */

/**
 * Columns that are set ONCE at creation and are never updatable — by anyone,
 * admins included. A work order's order number and creator are part of its
 * identity / audit trail; rewriting them would corrupt history.
 */
export const IMMUTABLE_WORK_ORDER_FIELDS = ['orderNumber', 'createdBy'] as const;

/**
 * Columns an admin may edit but everyone else may not. `createdAt` (the "Date
 * of Order") is an audit field — a property manager correcting a work order
 * should not be able to back-date when it was opened. Disabled in the form and
 * dropped server-side for non-admins.
 *
 * Deliberately ABSENT: `pmApproved` / `approvedBy`. Approval is a real PM
 * capability, authorized separately by the approval gate in api/work-orders.ts.
 * Locking it here would break PM approvals — so it is "ignored unless explicitly
 * granted" by that gate, not by this list.
 */
export const NON_ADMIN_LOCKED_WORK_ORDER_FIELDS = ['createdAt'] as const;

export type NonUpdatableWorkOrderField =
	| typeof IMMUTABLE_WORK_ORDER_FIELDS[number]
	| typeof NON_ADMIN_LOCKED_WORK_ORDER_FIELDS[number];

type EditUser = Pick<User, 'id' | 'role' | 'isAdmin'> | null | undefined;
type EditWorkOrder = Pick<WorkOrder, 'createdBy' | 'status'>;
type EditAssociation = Pick<Association, 'assignedTo'> | null | undefined;

export interface WorkOrderEditAccess {
	user: EditUser;
	workOrder: EditWorkOrder;
	/**
	 * The work order's CURRENT association. Required to scope property managers
	 * to "their own" work orders. `null`/`undefined` (e.g. still loading, or a
	 * work order with no association) fails closed for a PM.
	 */
	association: EditAssociation;
	/**
	 * Holds the `association_admin` capability — the one thing that lifts a property manager's
	 * "their own associations" scope, on this side and in `api/work-orders.ts` alike. Optional so
	 * a caller that has not loaded flags fails CLOSED (scoped).
	 */
	isAssociationAdmin?: boolean;
	/**
	 * Holds `work_order_admin`, which the server maps to `UPDATE_WORK_ORDERS`. Optional so a
	 * caller that has not loaded flags fails CLOSED.
	 */
	isWorkOrderAdmin?: boolean;
}

/** A work order the user created that is still in the Open status (#119). */
export function isOpenWorkOrderCreator(user: EditUser, workOrder: EditWorkOrder): boolean {
	return Boolean(user && user.id === workOrder.createdBy && workOrder.status === WorkOrderStatus.Open);
}

/**
 * Whether the given association is one this property manager is responsible for.
 * Admins are not association-scoped (handled by the caller); this is the PM
 * "their own" rule used by both the edit affordance and the API.
 */
export function isAssignedPropertyManager(user: EditUser, association: EditAssociation): boolean {
	return Boolean(user && association && association.assignedTo === user.id);
}

/**
 * May this user edit this SPECIFIC work order through the shared edit form?
 *
 *   - Admin ROLE: any work order.
 *   - Creator of an Open work order: yes, regardless of role (#119).
 *   - Property Manager: only work orders whose association is assigned to them —
 *     i.e. "their own" — unless they hold `association_admin`. A PM must NOT be able
 *     to edit another PM's work orders otherwise.
 *   - Support: any work order (org-wide support is not association-scoped).
 *   - `work_order_admin` holders (Accounting, Maintenance): any work order. A PM holder stays
 *     association-scoped — the server lifts that scope only for `association_admin`.
 *   - Everyone else: no (their narrower paths — maintenance/contractor — are
 *     handled directly in the API and via the assigned-user inline editors).
 *
 * This is the single predicate the UI and the API agree on, so a PM never sees
 * an edit button the server would reject and vice-versa. NOT the retired `is_admin`
 * column (#500): the server stopped honouring it, so reading it here would offer an
 * edit to a flagged PM that 403s, and hide one from an Admin created since. The
 * `work_order_admin` branch is what replaced `is_admin` for non-Admin-role holders —
 * without it, an accounting user with full work-order admin lost the pencil entirely.
 */
export function canEditWorkOrder({
	user, workOrder, association, isAssociationAdmin, isWorkOrderAdmin,
}: WorkOrderEditAccess): boolean {
	if (!user) return false;
	if (user.role === UserRole.Admin) return true;
	if (isOpenWorkOrderCreator(user, workOrder)) return true;

	switch (user.role) {
		case UserRole.PropertyManager:
			return !!isAssociationAdmin || isAssignedPropertyManager(user, association);
		case UserRole.Support:
			return true;
		case UserRole.Contractor:
			return false;
		default:
			return !!isWorkOrderAdmin;
	}
}

/**
 * Field keys this user may NOT modify on an update. The Admin role and `work_order_admin`
 * holders are blocked only from the immutable identity columns; everyone else is additionally
 * blocked from the audit columns (the Date of Order correction). The form disables these
 * inputs; the API deletes them from the request body before applying the update.
 *
 * `isWorkOrderAdmin` is passed in because this module is shared: the server resolves it with
 * `hasPermission(FeatureFlagName.WorkOrderAdmin, user)`, the client with `usePermissions()`.
 * NOT the retired `is_admin` column (#500) — it locked out every Admin created since.
 */
export function getNonUpdatableWorkOrderFields(
	user: EditUser,
	isWorkOrderAdmin = false,
): Set<NonUpdatableWorkOrderField> {
	const fields = new Set<NonUpdatableWorkOrderField>(IMMUTABLE_WORK_ORDER_FIELDS);
	if (user?.role !== UserRole.Admin && !isWorkOrderAdmin) {
		for (const field of NON_ADMIN_LOCKED_WORK_ORDER_FIELDS) {
			fields.add(field);
		}
	}
	return fields;
}

/**
 * The Date of Order the form submits. The picker holds only a DAY, so when that day is still
 * the stored moment's ET day the stored moment goes back verbatim — otherwise saving an edit
 * would rewrite a real timestamp to the picker's 05:00Z marker without anyone touching it.
 */
export function submittedCreatedAt(picked: Date | null, stored: Date | string | null | undefined): string {
	const day = picked ?? todayBusinessDate();
	if (stored) {
		const storedMs = new Date(stored).getTime();
		if (Number.isFinite(storedMs) && toBusinessMs(day) === etDayMs(storedMs)) return new Date(storedMs).toISOString();
	}
	return day.toISOString();
}

/**
 * 17:00Z — noon EST / 1 p.m. EDT. `created_at` is rendered as a real moment in the VIEWER'S
 * zone, and at this hour the calendar day is the same in UTC and every US zone (Eastern through
 * Hawaii) year-round, so a back-dated order shows the picked day to every viewer.
 */
const BACKDATED_TIME_OF_DAY_MS = 17 * 3_600_000;

export type CreatedAtResolution = { ok: true; createdAt: number } | { ok: false; message: string };

/**
 * The `created_at` to store for a NEW work order, given the form's "Date of Order".
 *
 * Same rule as edit (`getNonUpdatableWorkOrderFields`): the Admin role or a `work_order_admin`
 * holder may set it; anyone else's value is silently ignored and they get `nowMs`.
 * `isWorkOrderAdmin` is resolved by the caller exactly as for edit, and defaults to false so a
 * caller that has not resolved it fails closed.
 *
 * The picked day itself is encoded by `encodeDateOfOrder`, the one encoding shared with edit.
 */
export function resolveCreateCreatedAt(
	user: EditUser,
	requested: unknown,
	nowMs: number,
	isWorkOrderAdmin = false,
): CreatedAtResolution {
	if (getNonUpdatableWorkOrderFields(user, isWorkOrderAdmin).has('createdAt')) return { ok: true, createdAt: nowMs };
	if (requested === undefined || requested === null || requested === '') return { ok: true, createdAt: nowMs };
	return encodeDateOfOrder(requested, nowMs);
}

export type EditCreatedAtResolution =
	| { ok: true; createdAt: number | undefined }
	| { ok: false; message: string };

/**
 * The `created_at` to write on EDIT, or `undefined` to leave the column alone.
 *
 * Same gate as create (including the caller-resolved `isWorkOrderAdmin`, failing closed), and a
 * CHANGED day goes through the same `encodeDateOfOrder`, so an edited
 * order and a created one store one encoding. Otherwise the edit form's 05:00Z picker marker was
 * stored verbatim and rendered as the PREVIOUS day west of Eastern.
 *
 * Left alone (`undefined`):
 *   - a caller who may not set it (the handler has already stripped it; this is the backstop);
 *   - an empty value. Clearing an audit column is not a supported edit, and the form never sends one;
 *   - any value on the stored moment's own ET day. The form sends the stored moment back verbatim
 *     when the day is untouched (`submittedCreatedAt`), and the day is all the picker can change.
 */
export function resolveEditCreatedAt(
	user: EditUser,
	requested: unknown,
	storedMs: number | null | undefined,
	nowMs: number,
	isWorkOrderAdmin = false,
): EditCreatedAtResolution {
	if (getNonUpdatableWorkOrderFields(user, isWorkOrderAdmin).has('createdAt')) return { ok: true, createdAt: undefined };
	if (requested === undefined || requested === null || requested === '') return { ok: true, createdAt: undefined };

	const requestedMs = new Date(requested as string | number).getTime();
	if (!Number.isFinite(requestedMs)) return { ok: false, message: 'Date of Order is not a valid date' };
	if (typeof storedMs === 'number' && Number.isFinite(storedMs) && etDayMs(requestedMs) === etDayMs(storedMs)) {
		return { ok: true, createdAt: undefined };
	}
	return encodeDateOfOrder(requested, nowMs);
}

/**
 * A picked Date of Order → the `created_at` moment to store. Shared by create and edit.
 *
 * The form's `DateInput` is a date-only picker that emits `YYYY-MM-DDT05:00:00Z`; the value is
 * read as its Eastern calendar day and compared against TODAY IN EASTERN. Today stores the exact
 * moment `nowMs`. A past day is stored at a fixed, zone-stable hour (`BACKDATED_TIME_OF_DAY_MS`)
 * rather than midnight UTC (the evening before, in the Americas) or the current time of day
 * (which after 8 p.m. or before ~3 a.m. Eastern lands on a neighbouring day for some US
 * viewers). A future day is refused rather than silently clamped.
 */
function encodeDateOfOrder(requested: unknown, nowMs: number): CreatedAtResolution {
	const requestedMs = new Date(requested as string | number).getTime();
	if (!Number.isFinite(requestedMs)) return { ok: false, message: 'Date of Order is not a valid date' };

	// The value's EASTERN day. For the picker's marker that is the same day as its UTC fields
	// (05:00Z is 00:00 EST / 01:00 EDT), and it also reads an ordinary timestamp correctly — a
	// 9 p.m. ET moment whose UTC day is already tomorrow is today, not the future.
	const pickedDay = etDayMs(requestedMs);
	const today = etDayMs(nowMs);
	if (pickedDay > today) return { ok: false, message: 'Date of Order cannot be in the future' };

	if (pickedDay === today) return { ok: true, createdAt: nowMs };
	return { ok: true, createdAt: pickedDay + BACKDATED_TIME_OF_DAY_MS };
}
